import { useState, useEffect, useContext, Fragment } from 'react';
import { AuthContext } from '../context/authContext';

const API = '/api';

export default function ChatHistorialPage() {
  const { token, role } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [usuario, setUsuario] = useState('');
  const [tipo, setTipo] = useState('');
  const [total, setTotal] = useState(0);
  const [promptAbierto, setPromptAbierto] = useState(null);

  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const cargar = async () => {
    setCargando(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (usuario.trim()) params.set('usuario', usuario.trim());
      if (tipo) params.set('engine', tipo);
      const res = await fetch(`${API}/obras/chat/historial?${params.toString()}`, { headers });
      if (!res.ok) throw new Error('Error al cargar el historial');
      const data = await res.json();
      setItems(data.historial || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message);
    }
    setCargando(false);
  };

  useEffect(() => {
    if (role !== 'admin') return;
    cargar();
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  if (role !== 'admin') {
    return (
      <div className="p-8 text-center text-gray-500">
        Acceso denegado. Solo administradores.
      </div>
    );
  }

  const etiquetaOrigen = (engine, proveedor) => {
    if (proveedor) return { label: `Modelo (${proveedor})`, tipo: 'modelo' };
    if (engine === 'gemini-rag') return { label: 'Modelo', tipo: 'modelo' };
    return { label: 'Plantilla', tipo: 'plantilla' };
  };

  const fecha = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('es-AR');
  };

  return (
    <div className="p-4 md:p-6">
      <h1 className="text-xl font-bold mb-4">Historial del Chat Inteligente</h1>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Usuario</label>
          <input
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
            placeholder="id o nombre de usuario"
            className="px-3 py-2 border rounded dark:bg-gray-800 dark:border-gray-600 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Cómo respondió</label>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            className="px-3 py-2 border rounded dark:bg-gray-800 dark:border-gray-600 text-sm"
          >
            <option value="">Todos</option>
            <option value="modelo">Modelo (LLM)</option>
            <option value="plantilla">Plantilla</option>
          </select>
        </div>
        <button
          onClick={cargar}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
        >
          Filtrar
        </button>
      </div>

      {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
      <p className="text-xs text-gray-500 mb-3">{total} interacciones</p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border dark:border-gray-700">
          <thead className="bg-gray-100 dark:bg-gray-800">
            <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
              <th className="p-2">Fecha</th>
              <th className="p-2">Usuario</th>
              <th className="p-2">Rol</th>
              <th className="p-2">Consulta</th>
              <th className="p-2">Respuesta</th>
              <th className="p-2">Origen</th>
              <th className="p-2">Prompt</th>
              <th className="p-2">Obras contexto</th>
              <th className="p-2">Valoración</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const origen = etiquetaOrigen(it.engine, it.proveedor_llm);
              return (
                <Fragment key={it.id}>
                <tr className="border-t dark:border-gray-700 align-top">
                  <td className="p-2 whitespace-nowrap text-xs">{fecha(it.creado)}</td>
                  <td className="p-2 text-xs">{it.usuario ?? '—'}</td>
                  <td className="p-2 text-xs">{it.rol ?? '—'}</td>
                  <td className="p-2 text-xs max-w-[220px]">{it.consulta}</td>
                  <td className="p-2 text-xs max-w-[300px]">{it.respuesta}</td>
                  <td className="p-2 text-xs">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        origen.tipo === 'modelo'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {origen.label}
                    </span>
                    {it.partido_filtro && (
                      <div className="text-[10px] text-gray-500 mt-1">{it.partido_filtro}</div>
                    )}
                  </td>
                  <td className="p-2 text-xs">
                    {it.prompt ? (
                      <button
                        type="button"
                        onClick={() => setPromptAbierto(promptAbierto === it.id ? null : it.id)}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {promptAbierto === it.id ? 'Ocultar' : 'Ver prompt'}
                      </button>
                    ) : (
                      <span className="text-[10px] text-gray-400">—</span>
                    )}
                  </td>
                  <td className="p-2 text-xs">{it.resultado_n ?? 0}</td>
                  <td className="p-2 text-xs">
                    {it.valoracion === 1 ? '👍' : it.valoracion === -1 ? '👎' : '·'}
                    {it.comentario && <div className="text-[10px] text-gray-500">{it.comentario}</div>}
                  </td>
                </tr>
                {promptAbierto === it.id && it.prompt && (
                  <tr className="border-t dark:border-gray-800">
                    <td colSpan="9" className="p-2">
                      <div className="text-[10px] uppercase text-gray-400 mb-1">Prompt enviado al LLM</div>
                      <pre className="text-[11px] font-mono bg-gray-50 dark:bg-gray-900 rounded p-3 max-h-72 overflow-auto whitespace-pre-wrap break-words">
                        {it.prompt}
                      </pre>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
            {!cargando && items.length === 0 && (
              <tr>
                <td colSpan="9" className="p-4 text-center text-gray-400 text-sm">
                  Sin interacciones registradas.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
